/**
 * DonorEventPublicPage — Fundraising Suite, donor management component D.
 *
 * Unauthenticated donor-facing event landing reached at /event/{id}. Mirrors the
 * DonationsPublicPage public pattern: self-fetches public event meta and posts an
 * RSVP (name + email) to the public donor-events endpoints (no auth / no X-CBO-ID).
 * Includes a "Give now" CTA that links to the public donation page /donate/{cbo_id}
 * so gifts made from the invite roll up to the same org.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { CalendarHeart, CalendarPlus, CheckCircle, Heart, Loader2, MapPin } from 'lucide-react';
import { PublicDonorEvent, getPublicDonorEvent, publicRsvpDonorEvent } from '@/services/api/donorEvents';

/** Build an .ics VCALENDAR string for a public event (no external deps). */
function buildIcs(ev: PublicDonorEvent): string {
  const toIcs = (iso?: string | null) =>
    iso ? new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '') : '';
  const esc = (s?: string | null) => (s || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  const loc = ev.location_type === 'virtual' ? 'Virtual event' : ev.location_address || '';
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//WeaveIn//Donor Events//EN',
    'BEGIN:VEVENT',
    `UID:donor-event-${ev.id}@weavein`,
    `DTSTART:${toIcs(ev.start_time)}`,
    ev.end_time ? `DTEND:${toIcs(ev.end_time)}` : '',
    `SUMMARY:${esc(ev.title)}`,
    `DESCRIPTION:${esc(ev.description)}`,
    `LOCATION:${esc(loc)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n');
}

function downloadIcs(ev: PublicDonorEvent) {
  const blob = new Blob([buildIcs(ev)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${ev.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'event'}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function fmtWhen(start?: string | null, end?: string | null): string {
  if (!start) return '';
  const s = new Date(start);
  const base = s.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (end) {
    const e = new Date(end);
    return `${base} – ${e.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }
  return base;
}

export default function DonorEventPublicPage({ eventId }: { eventId: string }) {
  const [ev, setEv] = useState<PublicDonorEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'accepted' | 'tentative' | 'declined'>('accepted');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getPublicDonorEvent(eventId);
        if (active) setEv(data);
      } catch {
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [eventId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await publicRsvpDonorEvent(eventId, {
        name: name.trim() || undefined,
        email: email.trim(),
        response_status: status,
      });
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (notFound || !ev) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center text-gray-600">This event page is not available.</CardContent>
        </Card>
      </div>
    );
  }

  const orgName = ev.org_name || 'our organization';
  const isVirtual = ev.location_type === 'virtual';
  const donateUrl = `/donate/${ev.cbo_id}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 flex items-center justify-center">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <CalendarHeart className="mx-auto mb-2 h-10 w-10 text-blue-600" />
          <h1 className="text-xl font-semibold text-gray-900">{ev.title}</h1>
          <p className="text-sm text-gray-500">Hosted by {orgName}</p>
        </CardHeader>
        <CardContent>
          <div className="mb-4 space-y-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
            <div className="font-medium">{fmtWhen(ev.start_time, ev.end_time)}</div>
            <div className="flex items-center gap-1 text-gray-500">
              <MapPin size={14} /> {isVirtual ? 'Virtual event' : ev.location_address || 'In person'}
            </div>
            {ev.description ? <p className="whitespace-pre-line pt-1 text-gray-600">{ev.description}</p> : null}
            <button
              onClick={() => downloadIcs(ev)}
              className="flex items-center gap-1 pt-1 text-xs font-medium text-blue-600 hover:underline"
            >
              <CalendarPlus size={13} /> Add to calendar
            </button>
          </div>

          {done ? (
            <div className="py-6 text-center">
              <CheckCircle className="mx-auto mb-3 h-12 w-12 text-green-500" />
              <p className="text-lg font-medium text-gray-900">You're on the list!</p>
              <p className="mt-1 text-sm text-gray-500">We've recorded your RSVP. We look forward to seeing you.</p>
              <div className="mt-5 flex justify-center gap-2">
                <Button variant="outline" className="gap-2" onClick={() => downloadIcs(ev)}>
                  <CalendarPlus size={15} /> Add to calendar
                </Button>
                <a href={donateUrl}>
                  <Button variant="outline" className="gap-2">
                    <Heart size={15} /> Give now
                  </Button>
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Will you attend?</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['accepted', 'tentative', 'declined'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatus(s)}
                      className={`rounded border px-2 py-2 text-sm font-medium capitalize ${
                        status === s
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {s === 'accepted' ? 'Yes' : s === 'tentative' ? 'Maybe' : 'No'}
                    </button>
                  ))}
                </div>
              </div>
              {error ? <p className="text-sm text-red-600">{error}</p> : null}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  'Submit RSVP'
                )}
              </Button>
              <a href={donateUrl} className="block text-center text-sm text-blue-600 hover:underline">
                Can't make it? Give a gift instead →
              </a>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
